import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { transitionHoverStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
/* Poolcorp Custom Code */

type Props = WidgetProps & HasCategoryContext;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // Poolcorp Custom Code
    subCategoryTitle?: TypographyPresentationProps;
    subCategoryTitleItem?: GridItemProps;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {
    container: {
        gap: 0,
        css: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["none", null, "block"], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    subCategoryTitleItem: {
        width: 12,
        css: css`
            margin-bottom: 20px;
        `,
    },
    subCategoryTitle: {
        weight: TYPOGRAPHY.weight.black,
        css: css`
            margin-bottom: 20px;
        `,
    },
    subCategoryNameLink: {
        css: css`
            ${transitionHoverStyles("color")}
            color: ${getColor("text.accent")};

            &:hover {
                text-decoration: underline;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 12px;
        `,
    },
};

const styles = categoryDetailLinkListStyles;

const CategoryDetailLinkList = ({ category }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {/* Poolcorp Custom Code */}
            <GridItem {...styles.subCategoryTitleItem}>
                <Typography {...styles.subCategoryTitle}>{translate("Categories")}</Typography>
            </GridItem>
            {/* Poolcorp Custom Code */}
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId.toString()} {...styles.subCategoryNameLinkItem}>
                    <SubCategoryLink subCategoryId={subCategoryId} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { subCategoryId: string }) => {
    return {
        category: getCategoryState(state, ownProps.subCategoryId).value,
    };
};

const SubCategoryLinkView = ({ category }: ReturnType<typeof mapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            href={category.path}
            {...styles.subCategoryNameLink}
            data-test-selector={`categoryDetailsLinkListLink_${category.id}`}
        >
            {category.shortDescription}
        </Link>
    );
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailLinkList),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
